import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'lb-inline-flex lb-items-center lb-justify-center lb-gap-2 lb-whitespace-nowrap lb-text-sm lb-font-medium lb-transition-colors focus-visible:lb-outline-none focus-visible:lb-ring-1 focus-visible:lb-ring-ring disabled:lb-pointer-events-none disabled:lb-opacity-50 [&_svg]:lb-pointer-events-none [&_svg]:lb-size-4 [&_svg]:lb-shrink-0 lb-cursor-pointer',
	{
		variants: {
			variant: {
				default:
					'!lb-bg-primary lb-text-primary-foreground lb-shadow hover:lb-bg-primary/90 lb-border-none',
				destructive:
					'lb-bg-destructive lb-text-destructive-foreground lb-shadow-sm hover:lb-bg-destructive/90',
				outline:
					'lb-border lb-border-solid lb-border-input lb-bg-transparent lb-shadow-sm hover:lb-bg-foreground hover:lb-text-background lb-text-[0.8125rem]',
				secondary:
					'lb-bg-secondary lb-text-secondary-foreground lb-shadow-sm hover:lb-bg-secondary/80',
				ghost: 'hover:lb-bg-accent hover:lb-text-accent-foreground lb-bg-transparent lb-border-none',
				link: 'lb-text-primary lb-underline-offset-4 hover:lb-underline'
			},
			size: {
				default: 'lb-h-9 lb-px-4 lb-rounded-lg lb-py-2',
				sm: 'lb-h-8 lb-rounded-lg lb-px-3 lb-text-xs',
				lg: 'lb-h-10 lb-rounded-lg lb-px-8',
				icon: 'lb-h-9 lb-w-9'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
	VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
